import { type InputHTMLAttributes, forwardRef, type ReactNode } from "react";
import { cn } from "../cn";
import { EuBand } from "./LicensePlate";

export interface LicensePlateInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "type"> {
  /**
   * Rendered after the input, inside the plate body (e.g. a lookup button).
   * Inert in this primitive — it is purely a slot; wiring an actual
   * license-plate lookup is left to the caller.
   */
  readonly actionSlot?: ReactNode;
}

/**
 * Sanitizes free-text input into a bare NL plate identifier: uppercased,
 * letters/digits only (any hyphens the user types are dropped here and the
 * canonical ones re-derived by {@link formatDutchPlate}). NL-focused — the
 * grouping and the field's max length assume Dutch plates.
 */
function sanitizePlateValue(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/**
 * Groups a raw NL plate (letters/digits, no separators) into its three
 * hyphen-separated segments by matching the RDW "sidecode" patterns (the
 * official Dutch registration formats, e.g. `XX-99-XX`, `99-XXX-9`). Anything
 * that doesn't match a known 6-character sidecode — partial input as the user
 * types, or older/edge formats — is returned as-is, so grouping only appears
 * once a value is actually a recognised plate.
 */
const NL_SIDECODES: readonly RegExp[] = [
  /^([A-Z]{2})(\d{2})(\d{2})$/, //  XX-99-99
  /^(\d{2})(\d{2})([A-Z]{2})$/, //  99-99-XX
  /^(\d{2})([A-Z]{2})(\d{2})$/, //  99-XX-99
  /^(\d{2})([A-Z]{2})([A-Z]{2})$/, // 99-XX-XX
  /^([A-Z]{2})([A-Z]{2})(\d{2})$/, // XX-XX-99
  /^([A-Z]{2})(\d{2})([A-Z]{2})$/, // XX-99-XX
  /^(\d{2})([A-Z]{3})(\d)$/, //     99-XXX-9
  /^(\d)([A-Z]{3})(\d{2})$/, //     9-XXX-99
  /^([A-Z]{2})(\d{3})([A-Z])$/, //  XX-999-X
  /^([A-Z])(\d{3})([A-Z]{2})$/, //  X-999-XX
  /^([A-Z]{3})(\d{2})([A-Z])$/, //  XXX-99-X
  /^([A-Z])(\d{2})([A-Z]{3})$/, //  X-99-XXX
  /^(\d)([A-Z]{2})(\d{3})$/, //     9-XX-999
  /^(\d{3})([A-Z]{2})(\d)$/, //     999-XX-9
];

function formatDutchPlate(raw: string): string {
  const v = sanitizePlateValue(raw);
  for (const sidecode of NL_SIDECODES) {
    const match = v.match(sidecode);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }
  return v;
}

/**
 * Editable Dutch (NL) license-plate input, styled to match the read-only
 * {@link LicensePlate} primitive (same yellow body / blue EU band, using the
 * same fixed plate colors in both light and dark mode). Form-agnostic like
 * `Input`/`Textarea`: it forwards a ref, works controlled or uncontrolled,
 * and defers label/error to `Field`.
 */
export const LicensePlateInput = forwardRef<
  HTMLInputElement,
  LicensePlateInputProps
>(({ className, actionSlot, onChange, ...props }, ref) => {
  return (
    <div
      className={cn(
        // Focus is expressed in the plate's own visual language rather than
        // the app's orange focus ring, which clashes badly against the yellow
        // body and reads as a validation warning. Instead the plate's existing
        // hairline edge darkens and thickens into a crisp black outline (how a
        // real plate's stamped border catches light) plus a soft ambient glow
        // for the "lifted" affordance. Keeps a 3:1-contrast, non-color-only
        // focus indicator, so WCAG 2.4.7/1.4.11 still hold.
        "inline-flex h-10 w-fit items-stretch overflow-hidden rounded-[5px] ring-1 ring-black/20 transition-shadow",
        "focus-within:outline-none focus-within:ring-2 focus-within:ring-black/70 focus-within:shadow-[0_0_0_3px_rgba(0,0,0,0.12)]",
        "dark:ring-white/20 dark:focus-within:ring-white/80 dark:focus-within:shadow-[0_0_0_3px_rgba(255,255,255,0.16)]",
        // Invalid still wins over focus: declared after, same specificity.
        "aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-destructive",
        className,
      )}
      // Mirrors `aria-invalid` from the wrapped <input> onto the outer
      // plate shell, since that's the element with the visible border.
      aria-invalid={props["aria-invalid"]}
    >
      <EuBand size="lg" />
      <input
        ref={ref}
        type="text"
        inputMode="text"
        autoComplete="off"
        spellCheck={false}
        // The backend identifier allows up to 10 chars (letters/digits/
        // hyphens); recognised 6-char sidecodes still render hyphenated to 8
        // ("AB-12-CD"), but longer/edge identifiers aren't blocked here.
        maxLength={10}
        onChange={(event) => {
          const formatted = formatDutchPlate(event.target.value);
          if (formatted !== event.target.value) {
            event.target.value = formatted;
          }
          onChange?.(event);
        }}
        // The inner input suppresses the app-wide `:focus-visible` ring from
        // globals.css — that orange ring is drawn *inside* the plate body and
        // shows through as a stray vertical sliver at the input's edges. The
        // wrapper's own focus-within treatment above is the focus indicator.
        className="w-[11ch] min-w-0 flex-1 bg-[#f4c404] px-3 text-center font-sans text-base font-bold uppercase tracking-[0.08em] text-[#15171c] outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:font-normal placeholder:tracking-normal placeholder:normal-case placeholder:text-[#15171c]/40"
        {...props}
      />
      {actionSlot && (
        <span className="flex shrink-0 items-center bg-[#f4c404] pr-1">
          {actionSlot}
        </span>
      )}
    </div>
  );
});
LicensePlateInput.displayName = "LicensePlateInput";
